import { z } from "zod";
import type { DraftPlan } from "./types";

/**
 * Inspector form schemas (ADR-0027 RHF + zod). FL-1 (`minCovers ≤ maxCovers`) is enforced at input
 * time here so a plan that violates it can never be saved — the editor is the ONLY authoring path for
 * the bounds the auto-assign engine later enforces (R2). Messages are i18n KEYS resolved by the form.
 */

export const SHAPES = ["ROUND", "SQUARE", "RECTANGLE", "OVAL", "CUSTOM"] as const;

export const tableSchema = z
  .object({
    label: z
      .string()
      .trim()
      .min(1, "floorPlan.error.labelRequired")
      .max(40, "floorPlan.error.labelTooLong"),
    shape: z.enum(SHAPES),
    x: z.number().finite(),
    y: z.number().finite(),
    rotationDeg: z.number().int().min(0).max(359),
    minCovers: z.number().int().min(1, "floorPlan.error.coversMin"),
    maxCovers: z.number().int().min(1, "floorPlan.error.coversMin"),
    priorityOrder: z.number().int().min(0),
  })
  .refine((d) => d.minCovers <= d.maxCovers, {
    path: ["maxCovers"],
    message: "floorPlan.error.coversOrder", // FL-1
  });

export type TableFormValues = z.infer<typeof tableSchema>;

export const zoneSchema = z
  .object({
    name: z
      .string()
      .trim()
      .min(1, "floorPlan.error.zoneNameRequired")
      .max(60, "floorPlan.error.zoneNameTooLong"),
    kind: z.enum(["SEATED_TABLES", "STANDING_HEADCOUNT"]),
    capacity: z.number().int().min(0).nullable(),
    legalCapacity: z.number().int().min(0).nullable(),
  })
  .refine((d) => d.kind !== "STANDING_HEADCOUNT" || (d.capacity ?? 0) > 0, {
    path: ["capacity"],
    message: "floorPlan.error.standingCapacityRequired", // R4: a standing zone needs a headcount
  })
  .refine((d) => d.legalCapacity === null || d.capacity === null || d.legalCapacity >= d.capacity, {
    path: ["legalCapacity"],
    message: "floorPlan.error.legalBelowCapacity",
  });

export type ZoneFormValues = z.infer<typeof zoneSchema>;

export const policySchema = z.object({
  oversizeCap: z.number().int().min(0, "floorPlan.error.oversizeCap"),
  spreadAcrossZones: z.boolean(),
});

export type PolicyFormValues = z.infer<typeof policySchema>;

export interface PlanValidation {
  ok: boolean;
  /** The i18n message key of the first blocking issue (undefined when the plan is valid). */
  message?: string;
  tableId?: string;
  zoneId?: string;
}

/**
 * Whole-draft gate for the Save button: every active table must pass FL-1 and every standing zone
 * must carry a capacity. Runs the same zod schemas the inspector uses, so a min>max on ANY table —
 * not just the selected one — blocks the save until fixed (acceptance: "blocks save until fixed").
 */
export function validatePlan(plan: DraftPlan): PlanValidation {
  for (const tb of plan.tables) {
    if (tb.retired) continue;
    const r = tableSchema.safeParse({
      label: tb.label,
      shape: tb.shape,
      x: tb.x,
      y: tb.y,
      rotationDeg: tb.rotationDeg,
      minCovers: tb.minCovers,
      maxCovers: tb.maxCovers,
      priorityOrder: tb.priorityOrder,
    });
    if (!r.success) {
      return { ok: false, message: r.error.issues[0]?.message, tableId: tb.id };
    }
  }
  for (const zn of plan.zones) {
    const r = zoneSchema.safeParse({
      name: zn.name,
      kind: zn.kind,
      capacity: zn.capacity,
      legalCapacity: zn.legalCapacity,
    });
    if (!r.success) {
      return { ok: false, message: r.error.issues[0]?.message, zoneId: zn.id };
    }
  }
  return { ok: true };
}
